import {
  createCondition,
  createGroup,
  ensureLogicRoot,
  type StyleRuleLogic,
} from '../map-style-workbench/builderLogic';

import { DEFAULT_LAYER_RULE_COLOR } from './types';

import type {
  LayerRuleBuilderMetadataV1,
  LayerRuleBuilderState,
  LayerRuleDefinition,
  LayerRuleLayerState,
} from './types';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const cloneRecord = (value: unknown): Record<string, unknown> | null => {
  if (!isRecord(value)) {
    return null;
  }
  return { ...value };
};

type PersistedLogic = LayerRuleBuilderMetadataV1['layers'][number]['rules'][number]['predicate'];

const deserializeLogic = (node: PersistedLogic | null | undefined): StyleRuleLogic => {
  if (!node || typeof node !== 'object') {
    return createGroup();
  }

  if ((node as any).type === 'condition') {
    const record = node as any;
    const fieldId = typeof record.field_id === 'string' ? record.field_id : '';
    const operatorId = typeof record.operator_id === 'string' ? record.operator_id : 'eq';
    const logic = createCondition(fieldId || 'field', operatorId || 'eq');
    logic.values = Array.isArray(record.values) ? record.values.map(String) : [];
    return logic;
  }

  if ((node as any).type === 'group') {
    const record = node as any;
    const operator = record.operator === 'OR' ? 'OR' : 'AND';
    const children = Array.isArray(record.children) ? (record.children as PersistedLogic[]).map(deserializeLogic) : [];
    return createGroup({ operator, children });
  }

  return createGroup();
};

const serializeLogic = (logic: StyleRuleLogic): PersistedLogic => {
  if (logic.type === 'condition') {
    return {
      type: 'condition',
      field_id: logic.fieldId,
      operator_id: logic.operatorId,
      values: Array.isArray(logic.values) ? logic.values.map(String) : [],
    };
  }

  return {
    type: 'group',
    operator: logic.operator === 'OR' ? 'OR' : 'AND',
    children: (logic.children ?? []).map(serializeLogic),
  };
};

export const loadLayerRuleBuilderState = (
  value: unknown,
): LayerRuleBuilderState => {
  if (!isRecord(value)) {
    return { version: 1, layers: [] };
  }

  const version = value.version;
  if (version !== 1) {
    return { version: 1, layers: [] };
  }

  const layersRaw = value.layers;
  if (!Array.isArray(layersRaw)) {
    return { version: 1, layers: [] };
  }

  const layers: LayerRuleLayerState[] = [];
  layersRaw.forEach(entry => {
    if (!isRecord(entry)) return;
    const sourceId = typeof entry.source_id === 'string' ? entry.source_id : '';
    const sourceAlias = typeof entry.source_alias === 'string' ? entry.source_alias : '';
    const sourceLayer = typeof entry.source_layer === 'string' ? entry.source_layer : '';
    const mode = 'hide_non_matching';

    if (!sourceId || !sourceAlias || !sourceLayer) return;

    const rulesRaw = entry.rules;
    const rules: LayerRuleDefinition[] = [];
    if (Array.isArray(rulesRaw)) {
      rulesRaw.forEach(ruleEntry => {
        if (!isRecord(ruleEntry)) return;
        const id = typeof ruleEntry.id === 'string' ? ruleEntry.id : '';
        if (!id) return;
        rules.push({
          id,
          enabled: ruleEntry.enabled !== false,
          label: typeof ruleEntry.label === 'string' ? ruleEntry.label : '',
          color: typeof ruleEntry.color === 'string' ? ruleEntry.color : DEFAULT_LAYER_RULE_COLOR,
          logic: ensureLogicRoot(deserializeLogic(ruleEntry.predicate as any)),
          representation:
            ruleEntry.representation === 'fill'
            || ruleEntry.representation === 'line'
            || ruleEntry.representation === 'circle'
            || ruleEntry.representation === 'symbol'
            || ruleEntry.representation === 'fill-extrusion'
              ? ruleEntry.representation
              : null,
          paint: cloneRecord(ruleEntry.paint),
          layout: cloneRecord(ruleEntry.layout),
        });
      });
    }

    layers.push({
      sourceId,
      sourceAlias,
      sourceLayer,
      mode,
      fields: [],
      rules,
    });
  });

  return { version: 1, layers };
};

export const serializeLayerRuleBuilderMetadata = (state: LayerRuleBuilderState): LayerRuleBuilderMetadataV1 => {
  const sources = new Map<string, string>();
  state.layers.forEach(layer => {
    if (layer.sourceId && layer.sourceAlias) {
      sources.set(layer.sourceId, layer.sourceAlias);
    }
  });

  return {
    version: 1,
    sources: Array.from(sources.entries()).map(([source_id, alias]) => ({ source_id, alias })),
    layers: state.layers.map(layer => ({
      source_id: layer.sourceId,
      source_alias: layer.sourceAlias,
      source_layer: layer.sourceLayer,
      mode: layer.mode,
      rules: layer.rules.map(rule => ({
        id: rule.id,
        enabled: rule.enabled,
        label: rule.label,
        color: rule.color,
        representation: rule.representation ?? null,
        paint: cloneRecord(rule.paint),
        layout: cloneRecord(rule.layout),
        predicate: serializeLogic(rule.logic),
      })),
    })),
  };
};
